// "Technische Details" modal — two tabs over the same pre-derived signals[].
//   Allgemeines Muster (general)  — training-population aggregates
//   Bei diesem Patienten (patient) — this patient's contribution today

import { useState } from "react"
import { RankedSignalDto, RankedVariantDto } from "pokal_api"
import AnalysisModal from "../shared/AnalysisModal"
import ShapBarsSection from "./ShapBarsSection"
import { featureIcon, featureLabel, variantBadge } from "./shapUtils"

type Props = {
    signals: RankedSignalDto[]
    onClose: () => void
}

type TabKey = "general" | "patient"

// Pearson r strength label used by the favorable/risk correlation blocks.
// |r| ≥ 0.5 → strong; 0.3 ≤ |r| < 0.5 → moderate.  Anything below 0.3 is
// already classified as "neutral" by Python and filtered out before reaching
// these blocks, so we don't need a third branch.
function strengthLabel(r: number): "stark" | "mäßig" {
    return Math.abs(r) >= 0.5 ? "stark" : "mäßig"
}

function correlationDescription(v: RankedVariantDto): string | null {
    if (v.generalDirection === "neutral" || v.pearsonR === null) return null
    const subject = v.kind === "std" ? "Höhere Variabilität" : "Höherer Durchschnittswert"
    const direction = v.pearsonR < 0 ? "niedrigerem" : "höherem"
    return `${subject} geht mit ${direction} PHQ-8-Wert einher`
}

// Tab 1 — Allgemeines Muster (training-population view).
function GeneralTab({ signals }: { signals: RankedSignalDto[] }) {
    if (signals.length === 0) {
        return (
            <p className={"text-sm text-gray-500"}>
                Kein SHAP-Artefakt verfügbar. Das Modell muss zuerst mit dem XAI-JSON im{" "}
                <span className={"font-semibold"}>xai</span>-Ordner in MLflow geloggt werden.
            </p>
        )
    }

    // Pre-derived correlation classifications — every variant's `generalDirection`
    // is already one of "increases" | "decreases" | "neutral".  We just group
    // the signals + variants by which bucket their meaningful variants fall
    // into.  "Schützend" = at least one variant with generalDirection=decreases;
    // "Risiko" = at least one with generalDirection=increases.  A signal may
    // appear in both buckets if its avg and std variants disagree.
    const favorableSignals: RankedSignalDto[] = signals
        .map((s) => ({
            ...s,
            variants: s.variants.filter((v) => v.generalDirection === "decreases"),
        }))
        .filter((s) => s.variants.length > 0)

    const unfavorableSignals: RankedSignalDto[] = signals
        .map((s) => ({
            ...s,
            variants: s.variants.filter((v) => v.generalDirection === "increases"),
        }))
        .filter((s) => s.variants.length > 0)

    const neutralVariantLabels: string[] = signals.flatMap((s) =>
        s.variants
            .filter((v) => v.generalDirection === "neutral")
            .map((v) => {
                const badge = variantBadge(v.kind)
                return badge ? `${featureLabel(s.groupKey)} (${badge})` : featureLabel(s.groupKey)
            }),
    )

    return (
        <div className={"space-y-6"}>
            <div className={"rounded-xl border border-gray-200 bg-gray-50 p-4"}>
                <div
                    className={
                        "mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500"
                    }
                >
                    Globale SHAP-Erklärung
                </div>
                <p className={"text-sm text-gray-700"}>
                    Diese Werte zeigen, wie stark und in welche Richtung jeder Faktor die
                    PHQ-8-Vorhersage im Durchschnitt über alle Trainingsdaten beeinflusst.
                    Positive Werte (rot) erhöhen die vorhergesagte Symptombelastung, negative
                    Werte (grün) senken sie.
                </p>
            </div>

            <ShapBarsSection signals={signals} source="global" mode="importance" />
            <ShapBarsSection signals={signals} source="global" mode="directional" />

            <div className={"space-y-4"}>
                <div>
                    <p className={"text-sm font-semibold text-gray-800"}>
                        Wie hängen Messgrößen mit dem PHQ-8-Score zusammen? (Trainingsdaten)
                    </p>
                    <div className={"mt-2 space-y-1 text-xs text-gray-500"}>
                        <p>
                            Ein grüner Balken bedeutet, dass höhere Werte typischerweise mit
                            niedrigerer Symptombelastung einhergingen; ein roter Balken mit
                            höherer. Die Balkenlänge zeigt die Stärke des Zusammenhangs.
                        </p>
                        <p>
                            <span className={"font-medium text-gray-700"}>stark</span>{" "}
                            = starker Zusammenhang (|r| ≥ 0,5){" · "}
                            <span className={"font-medium text-gray-700"}>mäßig</span>{" "}
                            = mäßiger Zusammenhang (0,3 ≤ |r| {"<"} 0,5){" · "}
                            <span className={"font-medium text-gray-700"}>
                                kein klarer Zusammenhang
                            </span>{" "}
                            = kein bedeutsamer linearer Zusammenhang (|r| {"<"} 0,3)
                        </p>
                    </div>
                </div>

                <CorrelationBlock
                    title="Schützende Faktoren"
                    titleColor="text-green-700"
                    barColor="bg-green-400"
                    signals={favorableSignals}
                />
                <CorrelationBlock
                    title="Risikofaktoren"
                    titleColor="text-red-700"
                    barColor="bg-red-400"
                    signals={unfavorableSignals}
                />

                {neutralVariantLabels.length > 0 && (
                    <p className={"text-xs text-gray-400"}>
                        Kein klarer Zusammenhang: {neutralVariantLabels.join(", ")}
                    </p>
                )}
            </div>
        </div>
    )
}

// Renders one block ("Schützende" / "Risikofaktoren") given pre-filtered
// signals whose `variants[]` have already been narrowed to the matching
// generalDirection.  Per-variant rendering shows a pearson_r bar + the
// strength label + the German one-liner description.
function CorrelationBlock({
    title,
    titleColor,
    barColor,
    signals,
}: {
    title: string
    titleColor: string
    barColor: string
    signals: RankedSignalDto[]
}) {
    if (signals.length === 0) return null

    return (
        <div className={"space-y-1"}>
            <p className={`text-xs font-semibold uppercase tracking-wide ${titleColor}`}>
                {title}
            </p>
            <div className={"divide-y divide-gray-100"}>
                {signals.map((signal) => {
                    const Icon = featureIcon(signal.groupKey)
                    return (
                        <div key={signal.groupKey} className={"py-2"}>
                            <div className={"flex items-center gap-3 px-3 pb-1"}>
                                <Icon className={"h-4 w-4 flex-shrink-0 text-gray-400"} />
                                <span className={"text-sm font-medium text-gray-600"}>
                                    {featureLabel(signal.groupKey)}
                                </span>
                            </div>
                            {signal.variants.map((v) => {
                                const badge = variantBadge(v.kind)
                                const r = v.pearsonR ?? 0
                                const desc = correlationDescription(v)
                                const strength = strengthLabel(r)
                                return (
                                    <div
                                        key={v.feature}
                                        className={"pl-10 pr-3 py-1 space-y-0.5"}
                                    >
                                        <div className={"flex items-center gap-2"}>
                                            {badge && (
                                                <span
                                                    className={
                                                        "flex-shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500"
                                                    }
                                                >
                                                    {badge}
                                                </span>
                                            )}
                                            <div
                                                className={
                                                    "flex-1 overflow-hidden rounded-full bg-gray-100"
                                                }
                                                style={{ height: "8px" }}
                                            >
                                                <div
                                                    className={`h-full rounded-full ${barColor}`}
                                                    style={{
                                                        width: `${Math.min(Math.abs(r) * 100, 100)}%`,
                                                    }}
                                                />
                                            </div>
                                            <span
                                                className={
                                                    "flex-shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600"
                                                }
                                            >
                                                {strength}
                                            </span>
                                        </div>
                                        {desc && (
                                            <p className={"text-xs italic text-gray-500"}>
                                                {desc}
                                            </p>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

// Tab 2 — Bei diesem Patienten (this patient's contribution today).
function PatientTab({ signals }: { signals: RankedSignalDto[] }) {
    if (signals.length === 0) {
        return (
            <p className={"text-sm text-gray-500"}>Keine lokalen SHAP-Daten verfügbar.</p>
        )
    }

    return (
        <div className={"space-y-6"}>
            <div className={"rounded-xl border border-gray-200 bg-gray-50 p-4"}>
                <div
                    className={
                        "mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500"
                    }
                >
                    Wie lese ich diese Werte?
                </div>
                <div className={"space-y-2 text-sm text-gray-700"}>
                    <p>
                        Die Balken zeigen, welche Messwerte dieses Patienten die Vorhersage
                        erhöhen oder senken.{" "}
                        <span className={"font-medium text-red-600"}>
                            Rot / &bdquo;erhöht&ldquo;
                        </span>
                        {" "}— dieser Faktor treibt die vorhergesagte Symptombelastung hoch.{" "}
                        <span className={"font-medium text-green-700"}>
                            Grün / &bdquo;gesenkt&ldquo;
                        </span>
                        {" "}— er zieht sie nach unten.
                    </p>
                    <p className={"text-gray-500"}>
                        Die Werte sind Mittelwerte über die jüngsten Patientendaten.{" "}
                        <span className={"font-medium"}>Ø</span> steht für den
                        Durchschnittswert über den Beobachtungszeitraum,{" "}
                        <span className={"font-medium"}>σ</span> für die Streuung der
                        Tageswerte.
                    </p>
                </div>
            </div>

            <ShapBarsSection signals={signals} source="local" mode="importance" />
            <ShapBarsSection signals={signals} source="local" mode="directional" />
        </div>
    )
}

export default function EinflussfaktorenDetailsModal({ signals, onClose }: Props) {
    const [tab, setTab] = useState<TabKey>("general")

    return (
        <AnalysisModal title="Technische Details: Einflussfaktoren" onClose={onClose}>
            <div className={"px-5 pt-4"}>
                <div
                    className={
                        "inline-flex rounded-lg border border-gray-200 bg-gray-100 p-0.5 text-sm"
                    }
                >
                    <button
                        type="button"
                        onClick={() => setTab("general")}
                        className={`rounded-md px-3 py-1.5 font-medium transition-colors ${
                            tab === "general"
                                ? "bg-white text-gray-900 shadow-sm"
                                : "text-gray-500 hover:text-gray-700"
                        }`}
                    >
                        Allgemeines Muster
                    </button>
                    <button
                        type="button"
                        onClick={() => setTab("patient")}
                        className={`rounded-md px-3 py-1.5 font-medium transition-colors ${
                            tab === "patient"
                                ? "bg-white text-gray-900 shadow-sm"
                                : "text-gray-500 hover:text-gray-700"
                        }`}
                    >
                        Bei diesem Patienten
                    </button>
                </div>
            </div>
            <div className={"px-5 py-5"}>
                {tab === "general" ? (
                    <GeneralTab signals={signals} />
                ) : (
                    <PatientTab signals={signals} />
                )}
            </div>
        </AnalysisModal>
    )
}
